import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { ScheduleGridProvider } from './ScheduleGridContext';
import { ScheduleGridDayColumn } from './ScheduleGridDayColumn';
import { ScheduleGridItem } from './ScheduleGridItem';
import { ScheduleGridNowIndicator } from './ScheduleGridNowIndicator';
import { ScheduleGridTimeColumn } from './ScheduleGridTimeColumn';
import { ScheduleDayGridProps, SizeThresholds } from './types';

const DEFAULT_HOUR_HEIGHT = 64;
const DEFAULT_GRID_LINES_PER_HOUR = 2;

export const DEFAULT_DAY_GRID_SIZE_THRESHOLDS: SizeThresholds = {
  minimal: 10,
  compact: 32,
};

export const ScheduleDayGrid = <T,>({
  items,
  timeRange,
  hourHeight = DEFAULT_HOUR_HEIGHT,
  gridLinesPerHour = DEFAULT_GRID_LINES_PER_HOUR,
  showTimeColumn = true,
  sizeThresholds = DEFAULT_DAY_GRID_SIZE_THRESHOLDS,
  showNowIndicator = false,
  nowIndicatorProps,
  ItemComponent,
  TooltipComponent,
  timeColumnProps,
  dayColumnProps,
}: ScheduleDayGridProps<T>) => {
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [items],
  );

  return (
    <ScheduleGridProvider
      hourHeight={hourHeight}
      gridLinesPerHour={gridLinesPerHour}
      timeRange={timeRange}
      sizeThresholds={sizeThresholds}
    >
      <Stack direction="row" sx={{ backgroundColor: 'background.paper' }}>
        {showTimeColumn && (
          <ScheduleGridTimeColumn
            sx={{ borderRight: 'none', '& ::before': { display: 'none' } }}
            {...timeColumnProps}
          />
        )}

        <ScheduleGridDayColumn sx={{ borderRight: 'none' }} {...dayColumnProps}>
          {showNowIndicator && <ScheduleGridNowIndicator {...nowIndicatorProps} />}
          {sortedItems.map((item) => (
            <ScheduleGridItem
              key={item.id}
              item={item}
              ItemComponent={ItemComponent}
              TooltipComponent={TooltipComponent}
            />
          ))}
        </ScheduleGridDayColumn>
      </Stack>
    </ScheduleGridProvider>
  );
};
